import {Flag, Tooltip} from 'platform/components';
import {Box, Heading, Hide, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CompareTrend} from '../../../components/CompareTrend/CompareTrend';
import i18n from '../../../i18n/i18n';
import {AvailableCar} from '../../../types/AvailableCar';
import {PriceReportType} from '../../../types/PriceReportType';
import {getDifference} from '../../../utils/getDifference';
import {getPrice} from '../../../utils/getPrice';
import {mapMetadaCurrency} from '../../../utils/mapMetadaCurrency';
import {HorizontalValue} from './HorizontalValue';
import {PriceChangesPopper} from './PriceChangesPopper';

interface VehicleCardContentPriceProps extends TestIdProps {
  vehicle: AvailableCar;
  priceReport: PriceReportType;
  isMyVehicle: boolean;
}

export function VehicleCardContentPrice(props: VehicleCardContentPriceProps) {
  const formatCurrency = useFormatCurrency();

  const vehiclePrice = getPrice(props.vehicle, props.priceReport);
  const priceDifference = getDifference(
    vehiclePrice,
    props.priceReport.sellingPrice.recommended.value
  );
  const currency = props.priceReport.sellingPrice.recommended.currency;
  const firstScrapedPrice = props.vehicle.first_scraped_price_with_vat ?? vehiclePrice;
  const firstScrapedPriceCurrency =
    mapMetadaCurrency(props.vehicle.first_scraped_price_currency) ?? currency;
  const shouldShowFirstScrapedPrice = firstScrapedPrice !== vehiclePrice;

  return (
    <>
      <Hide onMobile>
        <Box flex={1}>
          <VStack align="flex-end">
            <Show when={shouldShowFirstScrapedPrice}>
              <HStack spacing={1} align="center">
                <PriceChangesPopper
                  vehicle={props.vehicle}
                  priceReport={props.priceReport}
                  popperPlacement="top-end"
                />
                <Text size="xxSmall" color="secondary" strikethrough>
                  {formatCurrency(firstScrapedPrice ?? vehiclePrice, firstScrapedPriceCurrency)}
                </Text>
              </HStack>
              <Space vertical={1} />
            </Show>
            <HStack spacing={1} align="center">
              <Show when={props.vehicle.vat_reclaimable}>
                <Tooltip label={i18n.t('general.labels.vatDeductible')}>
                  <Flag
                    colorScheme="black"
                    size="small"
                    label={i18n.t('general.labels.vat')}
                    data-testid={suffixTestId('vatDeductible', props)}
                  />
                </Tooltip>
              </Show>
              <Heading size={5} data-testid={suffixTestId('price', props)}>
                {formatCurrency(vehiclePrice, currency)}
              </Heading>
            </HStack>
            <Hide when={props.isMyVehicle}>
              <Space vertical={1} />
              <CompareTrend
                value={priceDifference}
                formatter={(value) => formatCurrency(value, currency) ?? ''}
                reverse
              />
            </Hide>
          </VStack>
        </Box>
      </Hide>

      <Show onMobile>
        <HStack justify="space-between">
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.charts.labels.price')}
          </Text>
          <VStack align="flex-end" spacing={1}>
            <Show when={shouldShowFirstScrapedPrice}>
              <HStack spacing={1} align="center">
                <PriceChangesPopper vehicle={props.vehicle} priceReport={props.priceReport} />
                <Text size="xxSmall" color="secondary" strikethrough>
                  {formatCurrency(firstScrapedPrice ?? vehiclePrice, firstScrapedPriceCurrency)}
                </Text>
              </HStack>
            </Show>
            <HorizontalValue
              value={vehiclePrice}
              difference={priceDifference}
              formatter={(value) => (isNotNil(value) && formatCurrency(value, currency)) || ''}
              differenceReverseColor
              isPrice
              hasVatFlag={props.vehicle.vat_reclaimable}
              isMyVehicle={props.isMyVehicle}
              data-testid={suffixTestId('price', props)}
            />
          </VStack>
        </HStack>
      </Show>
    </>
  );
}
